import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Info } from 'lucide-react';

// Sample sentences (~22 words) so every size choice produces several chunks and
// auto's character-based breaks land visibly differently from fixed word counts.
const SAMPLES = {
  hinglish: 'Aaj main aapko ek aisi kamaal ki cheez dikhane wala hoon jo aapne apni poori zindagi mein pehle kabhi nahi dekhi hogi',
  hindi: 'आज मैं आपको एक ऐसी कमाल की चीज़ दिखाने वाला हूँ जो आपने अपनी पूरी ज़िंदगी में पहले कभी नहीं देखी होगी',
  marathi: 'Aaj mi tumhala ek ashi kamaal chi goshta dakhavnar aahe ji tumhi tumchya purna ayushyat aadhi kadhich pahili nasel',
  marathi_devanagari: 'आज मी तुम्हाला एक अशी कमाल ची गोष्ट दाखवणार आहे जी तुम्ही तुमच्या पूर्ण आयुष्यात आधी कधीच पाहिली नसेल',
  english: 'Today I am going to show you something absolutely amazing that you have never ever seen before in your entire life',
  banglish: 'Aj ami tomader emon ekta darun jinish dekhabo ja tomra tomader puro jibone age kokhono dekhoni',
  bengali: 'আজ আমি তোমাদের এমন একটা দারুণ জিনিস দেখাবো যা তোমরা তোমাদের পুরো জীবনে আগে কখনো দেখোনি',
  tenglish: 'Eeroju nenu meeku mee life lo eppudu chudani oka adbhutamaina vishayam chupistha',
  telugu: 'ఈరోజు నేను మీకు మీ జీవితంలో ఎప్పుడూ చూడని ఒక అద్భుతమైన విషయం చూపిస్తాను',
  tanglish: 'Innaiku naan ungaluku unga life la eppavum paakadha oru arumaiyana vishayam kaatta poren',
  tamil: 'இன்னைக்கு நான் உங்களுக்கு உங்க வாழ்க்கையில எப்பவும் பாக்காத ஒரு அருமையான விஷயம் காட்டப் போறேன்',
  gujlish: 'Aaje hu tamne tamari life ma kyarey na joyeli ek jordaar vastu batavvano chhu',
  gujarati: 'આજે હું તમને તમારી લાઈફમાં ક્યારેય ના જોયેલી એક જોરદાર વસ્તુ બતાવવાનો છું',
  roman_urdu: 'Aaj main aap ko aik aisi zabardast cheez dikhane wala hoon jo aap ne apni poori zindagi mein pehle kabhi nahi dekhi hogi',
  urdu: 'آج میں آپ کو ایک ایسی زبردست چیز دکھانے والا ہوں جو آپ نے اپنی پوری زندگی میں پہلے کبھی نہیں دیکھی ہوگی',
};

// Mirrors backend/src/services/segmentationService.js: fixed N words per line, or
// for 'auto' pack words up to the per-language character budget (AUTO_CHAR_LIMITS
// there — keep these values in sync). Backend's invalid-maxWords fallback is 6.
const AUTO_CHAR_LIMITS = { english: 42, hinglish: 42, hindi: 36, marathi: 42, marathi_devanagari: 36, banglish: 42, bengali: 36, tenglish: 42, telugu: 32, tanglish: 42, tamil: 26, gujlish: 42, gujarati: 36, roman_urdu: 42, urdu: 38 };
const autoLimit = (language) => AUTO_CHAR_LIMITS[language] ?? 42;
const chunkWords = (text, maxWords, language) => {
  const words = text.split(' ');
  const chunks = [];
  if (maxWords === 'auto') {
    const limit = autoLimit(language);
    let current = [];
    for (const w of words) {
      if (current.length && [...current, w].join(' ').length > limit) {
        chunks.push(current.join(' '));
        current = [w];
      } else {
        current.push(w);
      }
    }
    if (current.length) chunks.push(current.join(' '));
  } else {
    const n = Math.max(1, parseInt(maxWords, 10) || 6);
    for (let i = 0; i < words.length; i += n) {
      chunks.push(words.slice(i, i + n).join(' '));
    }
  }
  return chunks;
};

// Display time scales with word count so the preview *feels* like real playback:
// short chunks flip fast (reels pacing), long chunks linger (YouTube pacing).
const MS_PER_WORD = 350;
const MIN_CHUNK_MS = 700;

/**
 * CaptionPreview — mini video frame inside the subtitle-settings modals that
 * re-chunks a sample sentence live as the user changes words-per-line/language.
 * With a local video `file` it shows a real frame from the user's own upload;
 * otherwise it falls back to a dark gradient mock.
 */
const CaptionPreview = ({ file, maxWords, language }) => {
  const [chunkIndex, setChunkIndex] = useState(0);
  // Real aspect ratio of the uploaded video (w/h). Defaults to 16:9 for the
  // no-file mock; updated once the video's dimensions are known so a portrait
  // Reel (9:16) shows in a portrait frame instead of being cropped by a
  // landscape box. < 1 means portrait.
  const [ratio, setRatio] = useState(16 / 9);
  const portrait = ratio < 1;

  const chunks = useMemo(
    () => chunkWords(SAMPLES[language] || SAMPLES.hinglish, maxWords, language),
    [language, maxWords]
  );
  const isAuto = maxWords === 'auto';

  // Restart the cycle from the first chunk whenever the setting changes —
  // state adjusted during render (not in an effect) per React guidance.
  const [prevChunks, setPrevChunks] = useState(chunks);
  if (prevChunks !== chunks) {
    setPrevChunks(chunks);
    setChunkIndex(0);
  }

  // The object URL must be created inside the effect (not useMemo): StrictMode
  // re-runs effects but not memos, so a memoized URL survives its own cleanup's
  // revoke and the <video> is left pointing at a dead blob (ERR_FILE_NOT_FOUND).
  const [videoUrl, setVideoUrl] = useState(null);
  useEffect(() => {
    if (!file || !file.type?.startsWith('video/')) {
      setVideoUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Reset to the default frame whenever the source changes, so a previous
  // video's orientation doesn't linger until the new one reports its size.
  // Adjusted during render (not in an effect) per React guidance, mirroring the
  // prevChunks reset above.
  const [prevVideoUrl, setPrevVideoUrl] = useState(videoUrl);
  if (prevVideoUrl !== videoUrl) {
    setPrevVideoUrl(videoUrl);
    setRatio(16 / 9);
  }

  useEffect(() => {
    let i = 0;
    let timer;
    const msFor = (chunk) => Math.max(MIN_CHUNK_MS, chunk.split(' ').length * MS_PER_WORD);
    const tick = () => {
      i = (i + 1) % chunks.length;
      setChunkIndex(i);
      timer = setTimeout(tick, msFor(chunks[i]));
    };
    timer = setTimeout(tick, msFor(chunks[0]));
    return () => clearTimeout(timer);
  }, [chunks]);

  return (
    <div className="mb-5">
      {/* Flex wrapper so the portrait frame derives its width from height ×
          aspect-ratio (a flex item isn't stretched on its main axis, unlike a
          block box whose auto width would fill the column and drop the ratio). */}
      <div className="flex justify-center">
      <div
        className="relative rounded-xl overflow-hidden bg-stone-900 border border-stone-200 dark:border-stone-800 shadow-inner"
        style={portrait
          // Portrait Reel: size by height and derive width, so the 9:16 frame
          // shows in full. The height is viewport-relative (min picks the
          // smaller) so a phone still has room for the controls + Generate
          // button below instead of the preview eating the whole modal.
          ? { aspectRatio: ratio, height: 'min(340px, 34vh)', maxWidth: '100%' }
          // Landscape/square: fill the column width at the real aspect ratio.
          : { aspectRatio: ratio, width: '100%' }}
      >
        {videoUrl ? (
          <video
            src={videoUrl}
            muted
            playsInline
            preload="auto"
            // Seek off frame 0 (often black), and capture the real aspect ratio
            // so the frame matches the video instead of cropping it.
            onLoadedMetadata={(e) => {
              const { videoWidth: w, videoHeight: h, duration } = e.target;
              if (w && h) setRatio(w / h);
              try { e.target.currentTime = Math.min(1, (duration || 2) / 2); } catch { /* ignore */ }
            }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#3730a3_0%,_#1e1b4b_45%,_#0c0a1d_100%)]" />
        )}

        {/* Dark scrim at the bottom so the caption pops like a real burn */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Mode badge — Auto and 8-words chunk similarly for average text, so say
            explicitly which rule is being previewed */}
        <span className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/55 backdrop-blur-sm text-white/90 text-[9px] font-jetbrains font-bold tracking-widest uppercase pointer-events-none">
          {isAuto ? `Auto · fits ~${autoLimit(language)} chars/line` : `${maxWords} words/line`}
        </span>

        {/* key={chunkIndex} re-runs the entrance animation on every flip */}
        <div className="absolute inset-x-3 bottom-3 flex justify-center pointer-events-none">
          <span
            key={chunkIndex}
            className="text-center text-white font-extrabold font-jakarta leading-snug px-2 text-[15px] sm:text-[17px] animate-in fade-in zoom-in-95 duration-150"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.8)' }}
          >
            {chunks[chunkIndex]}
          </span>
        </div>
      </div>
      </div>{/* end flex wrapper */}
      <div className="mt-2 flex items-center justify-center gap-1.5 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 rounded-lg px-3 py-1.5">
        <Info className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 shrink-0" />
        <p className="text-[11px] text-indigo-700 dark:text-indigo-400 font-jakarta font-semibold">
          Preview only, your actual captions will come from your video&apos;s audio
        </p>
      </div>
    </div>
  );
};

CaptionPreview.propTypes = {
  file: PropTypes.object,
  maxWords: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  language: PropTypes.string,
};

export default CaptionPreview;
